import { useState, useEffect, useMemo } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Database,
  RefreshCw,
  Loader2,
  Search,
  HardDrive,
  Table2,
  AlertTriangle,
  ArrowUpDown,
  Trash2,
  ShieldAlert,
  CheckCircle2,
  X,
} from "lucide-react";

/**
 * AdminDbStats · Feb-2026
 * ------------------------------------------------------------------
 * Panel temporal de diagnóstico de tamaño de tablas en el RDS de
 * producción. Consume /api/admin/db-stats (read-only, sólo super_admin).
 * Sirve para decidir cuándo justifica un VACUUM FULL sobre qué tablas.
 */
export default function AdminDbStats() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("total_bytes");
  const [sortDir, setSortDir] = useState("desc");

  // Cleanup destructivo
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [vacuumFull, setVacuumFull] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  const load = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const { data: res } = await api.get("/admin/db-stats");
      setData(res);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error desconocido";
      setError(msg);
      toast.error("No se pudo cargar db-stats: " + msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const runCleanup = async () => {
    if (confirmText !== "DROP-AND-VACUUM") {
      toast.error("Debes escribir exactamente DROP-AND-VACUUM para confirmar");
      return;
    }
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      // 1) Kickoff → devuelve 202 con job_id inmediatamente
      const { data: kickoff } = await api.post("/admin/db-cleanup", {
        confirm: "DROP-AND-VACUUM",
        vacuum_full: vacuumFull,
        vacuum_analyze: !vacuumFull,
      });
      const jobId = kickoff.job_id;
      if (!jobId) throw new Error("Respuesta sin job_id");
      toast.info(`Cleanup lanzado en background · job ${jobId}. Haciendo seguimiento…`);
      setShowConfirm(false);
      setConfirmText("");
      setCleanupResult({ ...kickoff, status: "pending" });

      // 2) Poll cada 3s hasta finished/failed (max 30 min)
      const started = Date.now();
      const MAX_MS = 30 * 60 * 1000;
      let last = null;
      // Simple loop con setTimeout — no bloqueante
      while (Date.now() - started < MAX_MS) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const { data: status } = await api.get("/admin/db-cleanup-status", {
            params: { job_id: jobId },
          });
          last = status;
          setCleanupResult(status);
          if (status.status === "finished" || status.status === "failed") break;
        } catch (pollErr) {
          // Si el pod se reinició, GET devuelve 404 — mostramos el estado y salimos
          const msg = pollErr?.response?.data?.detail || pollErr.message;
          setCleanupResult({
            job_id: jobId,
            status: "unknown",
            error:
              "El seguimiento se interrumpió (pod reiniciado?). Revisa los logs del backend. Detalle: " +
              msg,
          });
          break;
        }
      }

      if (last?.status === "finished") {
        toast.success(`Cleanup terminado · liberados ${last.freed_pretty || "0 B"}`);
        await load();
      } else if (last?.status === "failed") {
        toast.error(`Cleanup falló: ${last.error || "sin detalle"}`);
      } else if (!last) {
        toast.warning("No se pudo obtener estado del job (timeout de polling).");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Error desconocido";
      toast.error("Cleanup falló al iniciarse: " + (typeof msg === "string" ? msg : JSON.stringify(msg)));
      setCleanupResult({ error: msg, status: "failed" });
    } finally {
      setCleanupRunning(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Filtrar + ordenar
  const rows = useMemo(() => {
    if (!data?.top_tables) return [];
    let list = data.top_tables.slice();
    if (q.trim()) {
      const needle = q.toLowerCase().trim();
      list = list.filter(
        (r) =>
          r.table_name?.toLowerCase().includes(needle) ||
          r.schema_name?.toLowerCase().includes(needle)
      );
    }
    list.sort((a, b) => {
      const va = a[sortBy] ?? 0;
      const vb = b[sortBy] ?? 0;
      if (typeof va === "string") {
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortDir === "asc" ? Number(va) - Number(vb) : Number(vb) - Number(va);
    });
    return list;
  }, [data, q, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const totalBytesTop = useMemo(
    () => (data?.top_tables || []).reduce((acc, r) => acc + Number(r.total_bytes || 0), 0),
    [data]
  );

  const totalRowsTop = useMemo(
    () => (data?.top_tables || []).reduce((acc, r) => acc + Number(r.row_count || 0), 0),
    [data]
  );

  const totalDeadTop = useMemo(
    () => (data?.top_tables || []).reduce((acc, r) => acc + Number(r.dead_rows || 0), 0),
    [data]
  );

  const maxBytes = useMemo(
    () => Math.max(1, ...(data?.top_tables || []).map((r) => Number(r.total_bytes || 0))),
    [data]
  );

  // Bloqueo defensivo — la ruta ya lo hace, pero refuerza en cliente
  if (user && user.role !== "super_admin" && user.role !== "admin") {
    return (
      <Layout>
        <div className="p-8">
          <div className="neu-card p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 text-amber-500" size={40} />
            <h2 className="text-xl font-bold" style={{ color: "#0A3747" }}>
              Acceso restringido
            </h2>
            <p className="text-sm mt-2" style={{ color: "#64748B" }}>
              Esta sección sólo está disponible para <b>super_admin</b>.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-db-stats-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
              style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }} />
              <span
                className="text-[10px] font-semibold uppercase tracking-widest"
                style={{ color: "#6B7000" }}
              >
                Administración · Diagnóstico
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>
              Tamaño de la base de datos
            </h1>
            <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
              Top-20 tablas por tamaño total (heap + índices + toast). Datos vivos de{" "}
              <code className="text-[11px] bg-gray-100 px-1.5 py-0.5 rounded">pg_catalog</code>.
              Solo lectura.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button
                onClick={() => { setShowConfirm(true); setConfirmText(""); setVacuumFull(false); }}
                disabled={cleanupRunning || refreshing}
                className="neu-btn"
                style={{ color: "#DC2626" }}
                data-testid="db-cleanup-open-btn"
                title="Ejecuta DROP TABLE sobre master_ot + odoo_mirror_*. Requiere confirmación."
              >
                <Trash2 size={14} /> Limpiar espejos Odoo
              </button>
            )}
            <button
              onClick={load}
              disabled={refreshing}
              className="neu-btn neu-btn-primary"
              data-testid="db-stats-refresh-btn"
            >
              {refreshing ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Refrescar
            </button>
          </div>
        </div>

        {/* Stats KPI */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={HardDrive}
            label="Base de datos"
            value={data?.database?.total_pretty ?? "—"}
            hint={data?.database?.name}
            tone="navy"
          />
          <StatCard
            icon={Table2}
            label="Tablas Top-20"
            value={data?.count ?? 0}
            hint="listadas debajo"
            tone="cyan"
          />
          <StatCard
            icon={ArrowUpDown}
            label="Filas Top-20"
            value={totalRowsTop.toLocaleString("es-EC")}
            hint="n_live_tup agregado"
            tone="green"
          />
          <StatCard
            icon={AlertTriangle}
            label="Filas muertas"
            value={totalDeadTop.toLocaleString("es-EC")}
            hint="candidato a VACUUM"
            tone={totalDeadTop > 10000 ? "amber" : "navy"}
          />
        </div>

        {/* Buscador */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-md flex-1 min-w-[220px]">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 pointer-events-none"
              style={{ color: "#0A3747" }}
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtrar por nombre de tabla o esquema..."
              className="neu-input pl-11"
              data-testid="db-stats-search"
            />
          </div>
          <span className="text-xs" style={{ color: "#64748B" }}>
            {rows.length} de {data?.top_tables?.length ?? 0} tablas mostradas · Suma:{" "}
            <b>{prettyBytes(totalBytesTop)}</b>
          </span>
        </div>

        {/* Tabla */}
        <div className="neu-card overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="py-16 text-center px-6">
              <AlertTriangle className="mx-auto mb-3 text-red-500" size={40} />
              <p className="text-sm font-semibold" style={{ color: "#7F1D1D" }}>
                {error}
              </p>
              <p className="text-xs mt-2" style={{ color: "#64748B" }}>
                Verifica que <code>POSTGRES_URL</code> esté configurada y que el usuario tenga rol{" "}
                <b>super_admin</b>.
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="py-16 text-center" style={{ color: "#64748B" }}>
              No hay tablas que coincidan con el filtro.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm neu-table" data-testid="db-stats-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3 font-medium text-left">#</th>
                    <SortHeader
                      label="Tabla"
                      col="table_name"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                    />
                    <SortHeader
                      label="Tamaño total"
                      col="total_bytes"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                      align="right"
                    />
                    <SortHeader
                      label="Datos"
                      col="table_bytes"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                      align="right"
                    />
                    <SortHeader
                      label="Índices"
                      col="indexes_bytes"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                      align="right"
                    />
                    <SortHeader
                      label="Filas vivas"
                      col="row_count"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                      align="right"
                    />
                    <SortHeader
                      label="Filas muertas"
                      col="dead_rows"
                      sortBy={sortBy}
                      sortDir={sortDir}
                      onClick={toggleSort}
                      align="right"
                    />
                    <th className="px-4 py-3 font-medium text-left">Último vacuum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const pct = (Number(r.total_bytes || 0) / maxBytes) * 100;
                    const deadPct =
                      Number(r.row_count) > 0
                        ? (Number(r.dead_rows) / Number(r.row_count)) * 100
                        : 0;
                    const deadTone =
                      deadPct >= 20 ? "text-red-600 font-semibold" : deadPct >= 5 ? "text-amber-600" : "";
                    return (
                      <tr
                        key={`${r.schema_name}.${r.table_name}`}
                        className="border-t border-gray-200 hover:bg-gray-50"
                        data-testid={`db-stats-row-${r.table_name}`}
                      >
                        <td className="px-4 py-3 text-gray-500 tabular-nums">{i + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium" style={{ color: "#0A3747" }}>
                            {r.table_name}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            schema: {r.schema_name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="font-semibold tabular-nums" style={{ color: "#0A3747" }}>
                            {r.total_pretty}
                          </div>
                          <div className="mt-1 h-1.5 w-24 ml-auto rounded-full bg-gray-200 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${pct}%`,
                                background:
                                  "linear-gradient(90deg, #0A3747 0%, #E0E621 100%)",
                              }}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                          {r.table_pretty}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                          {r.indexes_pretty}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700">
                          {Number(r.row_count || 0).toLocaleString("es-EC")}
                        </td>
                        <td className={`px-4 py-3 text-right tabular-nums ${deadTone}`}>
                          {Number(r.dead_rows || 0).toLocaleString("es-EC")}
                          {deadPct > 0 && (
                            <span className="text-[10px] ml-1 opacity-70">
                              ({deadPct.toFixed(1)}%)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-gray-600">
                          {fmtDate(r.last_vacuum) || fmtDate(r.last_autovacuum) || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* v80.0 · Buscadores ILIKE en tablas PG */}
        <DbSearchPanel />

        <div className="text-[11px]" style={{ color: "#64748B" }}>
          Fuente: <code>pg_class</code> + <code>pg_stat_user_tables</code>. Endpoint temporal:{" "}
          <code>GET /api/admin/db-stats</code>. Retirar tras la ventana de mantenimiento.
        </div>

        {/* Resultado del último cleanup */}
        {cleanupResult && (
          <div
            className="neu-card p-5 space-y-3 border-l-4"
            style={{
              borderLeftColor:
                cleanupResult.status === "finished"
                  ? "#10B981"
                  : cleanupResult.status === "failed"
                  ? "#DC2626"
                  : cleanupResult.status === "running" || cleanupResult.status === "pending"
                  ? "#3B82F6"
                  : "#94A3B8",
            }}
            data-testid="db-cleanup-result-panel"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                {cleanupResult.status === "finished" && (
                  <CheckCircle2 size={22} className="text-emerald-600" />
                )}
                {cleanupResult.status === "failed" && (
                  <AlertTriangle size={22} className="text-red-600" />
                )}
                {(cleanupResult.status === "running" || cleanupResult.status === "pending") && (
                  <Loader2 size={22} className="animate-spin text-blue-600" />
                )}
                {(!cleanupResult.status || cleanupResult.status === "unknown") && (
                  <AlertTriangle size={22} className="text-amber-500" />
                )}
                <div>
                  <div className="font-semibold" style={{ color: "#0A3747" }}>
                    {cleanupResult.status === "finished" && "Cleanup terminado"}
                    {cleanupResult.status === "failed" && "Cleanup falló"}
                    {cleanupResult.status === "pending" && "Cleanup encolado…"}
                    {cleanupResult.status === "running" && "Cleanup en progreso…"}
                    {cleanupResult.status === "unknown" && "Estado desconocido"}
                    {!cleanupResult.status && "Cleanup"}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                    {cleanupResult.executed_by && (
                      <>Por <b>{cleanupResult.executed_by}</b></>
                    )}
                    {cleanupResult.job_id && (
                      <>· job <code className="text-[10px]">{cleanupResult.job_id}</code></>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setCleanupResult(null)}
                className="neu-icon-btn"
                aria-label="Cerrar"
                data-testid="db-cleanup-result-close"
                disabled={
                  cleanupResult.status === "running" || cleanupResult.status === "pending"
                }
              >
                <X size={14} />
              </button>
            </div>

            {/* Barra de progreso (mientras corre) */}
            {(cleanupResult.status === "running" || cleanupResult.status === "pending") &&
              cleanupResult.progress && (
                <div>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-gray-600 font-medium">
                      {progressLabel(cleanupResult.progress)}
                      {cleanupResult.progress.current_table && (
                        <>
                          {" · "}
                          <code className="text-[11px]">{cleanupResult.progress.current_table}</code>
                        </>
                      )}
                    </span>
                    <span className="text-gray-500 tabular-nums">
                      {cleanupResult.progress.done ?? 0} / {cleanupResult.progress.total ?? 0}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          cleanupResult.progress.total > 0
                            ? Math.round(
                                (cleanupResult.progress.done / cleanupResult.progress.total) * 100
                              )
                            : 0
                        }%`,
                        background: "linear-gradient(90deg, #0A3747 0%, #3B82F6 100%)",
                      }}
                    />
                  </div>
                </div>
              )}

            {cleanupResult.error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-800">
                <b>Error:</b> {typeof cleanupResult.error === "string" ? cleanupResult.error : JSON.stringify(cleanupResult.error)}
              </div>
            )}

            {cleanupResult.database && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <MiniStat label="BD antes" value={cleanupResult.database?.before_pretty} />
                <MiniStat label="BD después" value={cleanupResult.database?.after_pretty || "…"} />
                <MiniStat
                  label="Espacio liberado"
                  value={cleanupResult.freed_pretty || cleanupResult.database?.freed_pretty || "…"}
                  highlight
                />
              </div>
            )}

            {cleanupResult.drops?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  DROPs ({cleanupResult.drops.length}/{cleanupResult.progress?.total ?? cleanupResult.drops.length})
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
                  {cleanupResult.drops.map((d) => (
                    <div
                      key={d.table}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                        d.status === "dropped" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
                      }`}
                    >
                      {d.status === "dropped" ? (
                        <CheckCircle2 size={14} />
                      ) : (
                        <AlertTriangle size={14} />
                      )}
                      <code className="text-xs">{d.table}</code>
                      <span className="text-[10px] opacity-70">
                        · {cleanupResult.tables_before?.[d.table]?.pretty || "—"}
                        {cleanupResult.tables_before?.[d.table]?.row_count > 0 &&
                          ` · ${cleanupResult.tables_before[d.table].row_count.toLocaleString("es-EC")} filas`}
                        {d.duration_sec != null && ` · ${d.duration_sec}s`}
                      </span>
                      {d.error && <span className="text-[10px] ml-auto">{d.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cleanupResult.vacuum?.length > 0 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  VACUUM
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  {cleanupResult.vacuum.map((v, i) => (
                    <div
                      key={i}
                      className={`px-3 py-1.5 rounded-md ${
                        v.status === "ok"
                          ? "bg-emerald-50 text-emerald-800"
                          : v.status === "skipped"
                          ? "bg-gray-100 text-gray-600"
                          : "bg-red-50 text-red-800"
                      }`}
                    >
                      {v.command || v.status} {v.error && `· ${v.error}`}
                      {v.reason && `· ${v.reason}`}
                      {v.duration_sec != null && ` · ${v.duration_sec}s`}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cleanupResult.status === "finished" && (
              <ul className="text-[11px] text-gray-600 list-disc list-inside space-y-0.5 pt-2 border-t border-gray-200">
                <li>Las tablas se recrean automáticamente en el próximo restart del backend o ciclo del scheduler Odoo.</li>
                <li>Los datos del cliente NO se perdieron: source of truth está en Odoo.</li>
                <li>Retirar este endpoint (<code>POST /api/admin/db-cleanup</code>) tras la ventana de mantenimiento.</li>
              </ul>
            )}

            <details className="text-[11px]">
              <summary className="cursor-pointer text-gray-500 hover:text-gray-800">
                Ver JSON completo
              </summary>
              <pre className="mt-2 p-3 bg-gray-900 text-gray-100 rounded-lg overflow-x-auto text-[10px] max-h-64">
                {JSON.stringify(cleanupResult, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>

      {/* Modal de confirmación destructiva */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10, 55, 71, 0.55)", backdropFilter: "blur(4px)" }}
          onClick={() => !cleanupRunning && setShowConfirm(false)}
          data-testid="db-cleanup-modal"
        >
          <div
            className="neu-card max-w-lg w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div
                className="rounded-full p-2.5 flex-shrink-0"
                style={{ background: "#FEE2E2" }}
              >
                <ShieldAlert size={22} className="text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold" style={{ color: "#0A3747" }}>
                  Confirmar operación destructiva
                </h3>
                <p className="text-sm mt-1" style={{ color: "#64748B" }}>
                  Se eliminarán las siguientes 8 tablas espejo. Los datos del cliente están seguros
                  (source of truth en Odoo), pero <b>OT SMART Inbox</b> y{" "}
                  <b>Odoo Explorer</b> quedarán vacíos hasta el próximo ciclo de sync.
                </p>
              </div>
            </div>

            <ul className="grid grid-cols-2 gap-1 text-[11px] font-mono bg-gray-50 rounded-lg p-3">
              <li>· master_ot</li>
              <li>· odoo_mirror_tasks</li>
              <li>· odoo_mirror_stock_quants</li>
              <li>· odoo_mirror_stock_lots</li>
              <li>· odoo_mirror_subscriptions</li>
              <li>· odoo_mirror_partners</li>
              <li>· odoo_mirror_products</li>
              <li>· odoo_mirror_stock_locations</li>
            </ul>

            <label className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-gray-50">
              <input
                type="checkbox"
                checked={vacuumFull}
                onChange={(e) => setVacuumFull(e.target.checked)}
                className="mt-0.5"
                data-testid="db-cleanup-vacuum-full-toggle"
              />
              <div>
                <div className="font-semibold text-red-700">
                  Ejecutar también VACUUM FULL global (bloquea la BD)
                </div>
                <div className="text-[11px] text-gray-600">
                  Sin marcar → sólo <code>VACUUM (ANALYZE)</code> (rápido, sin lock). El DROP ya
                  libera el espacio de las tablas eliminadas automáticamente.
                </div>
              </div>
            </label>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 mb-1.5">
                Escribe <code className="bg-gray-100 px-1.5 py-0.5 rounded">DROP-AND-VACUUM</code>{" "}
                para confirmar:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="neu-input"
                placeholder="DROP-AND-VACUUM"
                data-testid="db-cleanup-confirm-input"
                autoFocus
                autoComplete="off"
              />
            </div>

            <div className="flex items-center gap-2 justify-end pt-2 border-t border-gray-200">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={cleanupRunning}
                className="neu-btn"
                data-testid="db-cleanup-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={runCleanup}
                disabled={cleanupRunning || confirmText !== "DROP-AND-VACUUM"}
                className="neu-btn neu-btn-primary"
                style={{
                  background: confirmText === "DROP-AND-VACUUM" ? "#DC2626" : undefined,
                  color: confirmText === "DROP-AND-VACUUM" ? "white" : undefined,
                }}
                data-testid="db-cleanup-execute-btn"
              >
                {cleanupRunning ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Ejecutando…
                  </>
                ) : (
                  <>
                    <Trash2 size={14} /> Ejecutar cleanup
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// v80.0 · Buscadores ILIKE en tablas PG
// ═══════════════════════════════════════════════════════════════════════
const DB_SEARCH_TABS = [
  {
    key: "passive-equipment",
    label: "Equipos Pasivos",
    endpoint: "/v1/admin/db-search/passive-equipment",
    columns: ["id", "name", "cpe_serial", "equipment_type", "ip_address", "is_free", "olt_id"],
    placeholder: "Buscar por nombre o serial de caja...",
  },
  {
    key: "master-ot",
    label: "Master OT",
    endpoint: "/v1/admin/db-search/master-ot",
    columns: ["odoo_task_id", "afc_code", "client_name", "task_name", "internal_status", "activity_type", "branch_name"],
    noActivityFilter: true,   // v97.0.0
    placeholder: "Buscar por cliente, código OT, nombre de tarea...",
  },
  {
    key: "solution-type-materials",
    label: "Materiales (Recetas)",
    endpoint: "/v1/admin/db-search/solution-type-materials",
    columns: ["product_name", "product_code", "product_category_name", "match_mode", "qty_min", "qty_max"],
    placeholder: "Buscar por producto, código o categoría...",
  },
];

function DbSearchPanel() {
  const [activeTab, setActiveTab] = useState(DB_SEARCH_TABS[0].key);
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [noActivity, setNoActivity] = useState(false);   // v97.0.0

  const tab = DB_SEARCH_TABS.find((t) => t.key === activeTab);

  const doSearch = async (q = searchQ, p = 1) => {
    setSearching(true);
    setResults(null);
    try {
      const params = { q: q.trim(), page: p, page_size: 25 };
      if (tab.noActivityFilter && noActivity) params.no_activity = true;
      const { data } = await api.get(tab.endpoint, { params });
      setResults(data);
      setPage(p);
    } catch (e) {
      toast.error("Error buscando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSearching(false);
    }
  };

  const handleTabSwitch = (key) => {
    setActiveTab(key);
    setSearchQ("");
    setResults(null);
    setPage(1);
  };

  return (
    <div className="neu-card overflow-hidden" data-testid="db-search-panel">
      <div className="px-5 py-3 flex items-center justify-between" style={{ background: "#0A3747", color: "#E0E621" }}>
        <div className="flex items-center gap-2">
          <Search size={16} />
          <span className="font-bold text-sm">Buscadores BD</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: "#E2E8F0" }}>
        {DB_SEARCH_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => handleTabSwitch(t.key)}
            className="px-4 py-2.5 text-xs font-semibold transition-all relative"
            style={{
              color: activeTab === t.key ? "#0A3747" : "#64748B",
              background: activeTab === t.key ? "#F8FAFC" : "transparent",
            }}
            data-testid={`db-search-tab-${t.key}`}
          >
            {t.label}
            {activeTab === t.key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: "#E0E621" }} />
            )}
          </button>
        ))}
      </div>

      {/* Search input */}
      <div className="px-5 py-4">
        <form
          onSubmit={(e) => { e.preventDefault(); doSearch(); }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "#94A3B8" }}
            />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={tab?.placeholder || "Buscar..."}
              className="neu-input pl-9 w-full text-xs"
              data-testid="db-search-input"
            />
          </div>
          {tab?.noActivityFilter && (
            <label className="flex items-center gap-1.5 text-xs font-semibold text-red-700 whitespace-nowrap" data-testid="db-search-no-activity">
              <input type="checkbox" checked={noActivity} onChange={(e) => setNoActivity(e.target.checked)} />
              ⚑ Solo sin tipo de actividad
            </label>
          )}
          <button
            type="submit"
            disabled={searching}
            className="neu-btn neu-btn-primary text-xs"
            data-testid="db-search-submit"
          >
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Buscar
          </button>
        </form>

        {/* Results */}
        {searching && (
          <div className="flex items-center justify-center gap-2 py-8">
            <Loader2 size={20} className="animate-spin" style={{ color: "#94A3B8" }} />
            <span className="text-xs text-gray-500">Buscando...</span>
          </div>
        )}
        {!searching && results && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px]" style={{ color: "#64748B" }}>
                {results.total} resultado{results.total !== 1 ? "s" : ""} · Página {results.page}
              </span>
              <div className="flex gap-1">
                <button
                  onClick={() => doSearch(searchQ, Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="text-[10px] px-2 py-1 rounded border disabled:opacity-30"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid="db-search-prev"
                >
                  Anterior
                </button>
                <button
                  onClick={() => doSearch(searchQ, page + 1)}
                  disabled={(results.items?.length || 0) < (results.page_size || 25)}
                  className="text-[10px] px-2 py-1 rounded border disabled:opacity-30"
                  style={{ borderColor: "#E2E8F0" }}
                  data-testid="db-search-next"
                >
                  Siguiente
                </button>
              </div>
            </div>
            {results.items?.length === 0 ? (
              <div className="text-center py-6 text-xs" style={{ color: "#94A3B8" }}>
                Sin resultados para esta búsqueda.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs neu-table" data-testid="db-search-results-table">
                  <thead>
                    <tr>
                      {tab.columns.map((col) => (
                        <th key={col} className="px-3 py-2 text-left font-medium text-gray-600 text-[10px] uppercase tracking-wider">
                          {col.replace(/_/g, " ")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.items.map((row, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                        {tab.columns.map((col) => (
                          <td key={col} className="px-3 py-2 text-[11px] max-w-[200px] truncate" style={{ color: "#0A3747" }}>
                            {col === "activity_type" && row.no_activity_type
                              ? <span className="px-1.5 py-0.5 rounded bg-red-600 text-white text-[9px] font-bold">⚑ SIN ACTIVIDAD</span>
                              : (row[col] === true ? "SI" : row[col] === false ? "NO" : row[col] ?? "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {!searching && !results && (
          <div className="text-center py-6 text-xs" style={{ color: "#94A3B8" }}>
            Escribe un término y presiona <strong>Buscar</strong> para consultar la tabla.
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════
function prettyBytes(b) {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString("es-EC", {
      timeZone: "America/Guayaquil", year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function progressLabel(p) {
  if (!p) return "";
  const map = {
    queued: "En cola",
    snapshot_before: "Midiendo tamaño inicial…",
    dropping: "Eliminando tablas",
    vacuum: "Ejecutando VACUUM…",
    snapshot_after: "Midiendo tamaño final…",
    done: "Terminado",
  };
  return map[p.step] || p.step;
}

function MiniStat({ label, value, highlight = false }) {
  return (
    <div
      className="rounded-xl px-3 py-2"
      style={{
        background: highlight
          ? "linear-gradient(145deg, #FEF9C3, #FDE68A)"
          : "linear-gradient(145deg, #F8FAFC, #E2E8F0)",
        boxShadow: "inset -1px -1px 3px rgba(255,255,255,0.6), inset 1px 1px 3px rgba(163,177,198,0.35)",
      }}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold text-gray-600">{label}</div>
      <div
        className="text-lg font-bold tabular-nums"
        style={{ color: highlight ? "#78350F" : "#0A3747" }}
      >
        {value ?? "—"}
      </div>
    </div>
  );
}

function SortHeader({ label, col, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === col;
  return (
    <th
      className={`px-4 py-3 font-medium cursor-pointer select-none hover:bg-gray-50 ${
        align === "right" ? "text-right" : "text-left"
      }`}
      onClick={() => onClick(col)}
      data-testid={`db-stats-sort-${col}`}
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-slate-900" : "text-gray-600"}`}>
        {label}
        <ArrowUpDown size={12} className={active ? "opacity-100" : "opacity-30"} />
        {active && <span className="text-[10px]">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone = "navy" }) {
  const styles = {
    navy: {
      bg: "linear-gradient(145deg, #F1F5F9, #E4EAF0)",
      fg: "#0A3747",
      ring: "rgba(163,177,198,0.55)",
    },
    green: {
      bg: "linear-gradient(145deg, #34D399, #10B981)",
      fg: "#064E3B",
      ring: "rgba(16,185,129,0.5)",
    },
    cyan: {
      bg: "linear-gradient(145deg, #93C5FD, #3B82F6)",
      fg: "#1E3A8A",
      ring: "rgba(59,130,246,0.5)",
    },
    amber: {
      bg: "linear-gradient(145deg, #FCD34D, #F59E0B)",
      fg: "#78350F",
      ring: "rgba(245,158,11,0.5)",
    },
  };
  const st = styles[tone] || styles.navy;
  return (
    <div
      className="rounded-2xl px-4 py-3 transition-all hover:-translate-y-0.5"
      style={{
        background: st.bg,
        boxShadow: `-6px -6px 14px rgba(255,255,255,0.75), 6px 6px 14px ${st.ring}, inset -1px -1px 3px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.5)`,
      }}
    >
      <div className="flex items-center justify-between">
        <div
          className="text-[10px] uppercase tracking-widest font-bold"
          style={{ color: st.fg, opacity: 0.75 }}
        >
          {label}
        </div>
        {Icon && <Icon size={16} style={{ color: st.fg, opacity: 0.6 }} />}
      </div>
      <div
        className="text-2xl font-bold mt-1 tabular-nums"
        style={{ color: st.fg, textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}
      >
        {value}
      </div>
      {hint && (
        <div className="text-[10px] mt-0.5" style={{ color: st.fg, opacity: 0.6 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
